import {
  createContext,
  type PropsWithChildren,
  useCallback,
  useContext,
} from 'react';
import type { Featureflag } from '../constants/featureFlags';
import { useExperiment } from './use-experiment';
import { useSessionStorage } from '@mantine/hooks';
import { getEnvBoolean } from '../../utils/env';

interface UserContextState {
  isAvailableToCurrentUser: boolean;
  originalIsAvailableToCurrentUser: boolean;
  featuresAvailable: Featureflag[];
  isFeatureEnabled: (key: Featureflag, enableOnVariant?: string) => boolean;
  isLoadingFlags: boolean;
  setIsAvailableToCurrentUserOverride: (isAvailable: boolean) => void;
  aiFiltersOverride: boolean | null;
  setAiFiltersEnabledOverride: (isAiFiltersEnabled: boolean) => void;
  voiceResponsesOverride: boolean | null;
  setVoiceResponsesEnabledOverride: (enabled: boolean) => void;
  originalVoiceInputEnabled: boolean;
  voiceInputEnabled: boolean;
  setVoiceInputEnabledOverride: (enabled: boolean) => void;
}

const STORAGE_KEY_AI_FILTERS = 'caddie_override_ai_filters_enabled';
const STORAGE_KEY_VOICE_RESPONSES = 'caddie_override_voice_responses_enabled';
const STORAGE_KEY_VOICE_INPUT = 'caddie_override_voice_input_enabled';
const STORAGE_KEY_AVAILABILITY = 'caddie_override_user_availability';
const isAllFeatureFlagsEnabled = getEnvBoolean('ALL_FEATURE_FLAGS_ENABLED');

export const UserContext = createContext<UserContextState>({
  isAvailableToCurrentUser: false,
  originalIsAvailableToCurrentUser: false,
  featuresAvailable: [],
  isFeatureEnabled: () => false,
  isLoadingFlags: false,
  setIsAvailableToCurrentUserOverride: () => undefined,
  aiFiltersOverride: null,
  setAiFiltersEnabledOverride: () => undefined,
  voiceResponsesOverride: null,
  setVoiceResponsesEnabledOverride: () => undefined,
  originalVoiceInputEnabled: false,
  voiceInputEnabled: false,
  setVoiceInputEnabledOverride: () => undefined,
});

export const UserContextProvider = ({ children }: PropsWithChildren) => {
  const { isAvailableToCurrentUser, featuresAvailable, isLoadingFlags } =
    useExperiment();
  const [
    isAvailableToCurrentUserOverride,
    setIsAvailableToCurrentUserOverride,
  ] = useSessionStorage<boolean | null>({
    key: STORAGE_KEY_AVAILABILITY,
    defaultValue: null,
  });

  const [aiFiltersEnabledOverride, setAiFiltersEnabledOverride] =
    useSessionStorage<boolean | null>({
      key: STORAGE_KEY_AI_FILTERS,
      defaultValue: null,
    });

  const [voiceResponsesEnabledOverride, setVoiceResponsesEnabledOverride] =
    useSessionStorage<boolean | null>({
      key: STORAGE_KEY_VOICE_RESPONSES,
      defaultValue: null,
    });

  const [voiceInputEnabledOverride, setVoiceInputEnabledOverride] =
    useSessionStorage<boolean | null>({
      key: STORAGE_KEY_VOICE_INPUT,
      defaultValue: null,
    });

  const effectiveIsAvailableToCurrentUser =
    isAvailableToCurrentUserOverride === null
      ? isAvailableToCurrentUser
      : isAvailableToCurrentUserOverride;

  const isFeatureEnabled = useCallback(
    (key: Featureflag) => {
      if (isAllFeatureFlagsEnabled) return true;
      if (featuresAvailable.length === 0) return false;
      return featuresAvailable.includes(key);
    },
    [featuresAvailable]
  );

  const originalVoiceInputEnabled = true;

  const effectiveVoiceInputEnabled =
    voiceInputEnabledOverride === null
      ? originalVoiceInputEnabled
      : voiceInputEnabledOverride;

  const value = {
    isAvailableToCurrentUser: effectiveIsAvailableToCurrentUser,
    originalIsAvailableToCurrentUser: isAvailableToCurrentUser,
    setIsAvailableToCurrentUserOverride,
    featuresAvailable,
    isFeatureEnabled,
    isLoadingFlags,
    aiFiltersOverride: aiFiltersEnabledOverride,
    setAiFiltersEnabledOverride,
    voiceResponsesOverride: voiceResponsesEnabledOverride,
    setVoiceResponsesEnabledOverride,
    originalVoiceInputEnabled,
    voiceInputEnabled: effectiveVoiceInputEnabled,
    setVoiceInputEnabledOverride,
  };

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
};

export const useUserContext = () => {
  const context = useContext(UserContext);
  if (context === undefined) {
    throw new Error('useUserContext must be used within a UserContextProvider');
  }
  return context;
};
